import {
  GetCurriculumInDistrictsByDistrictID,
  MasterInfoResource,
} from '../../providers/resources'
import { ICurricula, IDistrictCurricula } from 'infrastructure/api'
import {
  IMasterInfo,
  IQueryInfo,
  doAdminQueries,
} from '../../providers/mutations'
import { ILeafRequest, INode } from 'shared/types/tree'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { openTab } from '../Helpers'
import { directTreeAccessUrl } from 'infrastructure/api'
import { useCallback } from 'react'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { allProductsByPrimaryTreeId } from '../../api/masters.atoms'
import { cloneDeep, processInChunks } from '../../utility'
import {
  appendNodeToTree,
  createLeaf,
  retrieveIds,
  updateTreeNode,
  useGetTree,
} from './TreeRequests'
import {
  makeKiddomDeleteRequest,
  makeKiddomGetRequest,
  makeKiddomPostRequest,
  makeKiddomPutRequest,
} from 'infrastructure/api'
import {
  useAdminQueryWithErrorNotification,
  useWithErrorNotification,
} from 'shared/ui/GlobalDialog'
import {
  isDistrictCurricula,
  isSchoolCurricula,
} from '../resources/curricula_components/utility'
import { isReferenceNode, nodesOfTypeWithContent } from '../utility/TreeHelpers'
import { idsForNode } from './nodeUpdates/utility'
import {
  contentFromNode,
  updateNodeContent,
} from './htmlContentUpdates/utility'
import { parseHtmlString } from 'shared/utils'

export interface IRootParams {
  treeId: string
  versionId: string
}

export interface ITreeParams extends IRootParams {
  nodeId?: string
}

interface ILeafParams extends IRootParams {
  leafId: string
}

export interface IWithNodeParams extends IRootParams {
  nodeId: string
}

interface IRootWithLeavesParams extends IRootParams {
  leaves: ILeafRequest[]
}

function deleteLeafRoute({ treeId, versionId, leafId }: ILeafParams) {
  return `/api/tree/${treeId}:${versionId}/leaf/${leafId}`
}

function bulkLeavesRoute({ treeId, versionId }: IRootParams) {
  return `/v1/tree/${treeId}:${versionId}/bulk_leaves`
}

export function getRootRoute({ treeId, versionId }: IRootParams) {
  return `/api/tree/${treeId}:${versionId}`
}

export function useGetTreeFromURL() {
  const getTree = useGetTree()
  return useCallback(
    (url: string) => {
      const urlParts = url.split('/')
      const lastSegment = urlParts[urlParts.length - 1]
      const ids = lastSegment.split(':')
      return getTree({ treeId: ids[0], versionId: ids[1] })
    },
    [getTree],
  )
}

export function bulkCreateLeaves({
  treeId,
  versionId,
  ...requestBody
}: {
  treeId: string
  versionId: string
  leaves: Record<string, ILeafRequest[]>
}) {
  const kiddomPath = `/v1/tree/${treeId}:${versionId}/bulk_leaves`
  return makeKiddomPostRequest({ kiddomPath, requestBody })
}

export function bulkCreateLeavesFromList({
  treeId,
  versionId,
  leaves,
}: IRootWithLeavesParams) {
  const leafMap: { [parentId: string]: ILeafRequest[] } = {}
  leaves.forEach((lr) => {
    if (!leafMap[lr.parent_id!]) {
      leafMap[lr.parent_id!] = []
    }
    leafMap[lr.parent_id!].push(lr)
  })
  return bulkCreateLeaves({ treeId, versionId, leaves: leafMap })
}

export function deleteNode({ treeId, versionId, nodeId }: IWithNodeParams) {
  const kiddomPath = `/api/tree/${treeId}:${versionId}/node/${nodeId}`
  return makeKiddomDeleteRequest<void>({ kiddomPath })
}

export function useDeleteNode() {
  const errorTitle = 'Error Deleting Node'
  return useWithErrorNotification({ errorTitle }, deleteNode)
}

export function deleteLeaf(params: ILeafParams) {
  const kiddomPath = deleteLeafRoute(params)
  return makeKiddomDeleteRequest<void>({ kiddomPath })
}

export function useDeleteLeaf() {
  const errorTitle = 'Error Deleting Leaf'
  return useWithErrorNotification({ errorTitle }, deleteLeaf)
}

export function bulkDeleteLeaves({
  leaves,
  ...ids
}: {
  treeId: string
  versionId: string
  leaves: string[]
}) {
  const kiddomPath = bulkLeavesRoute(ids)
  return makeKiddomDeleteRequest({ kiddomPath, requestBody: { leaves } })
}

export function useCreateTree() {
  const request = useWithErrorNotification(
    { errorTitle: 'Error Creating Tree' },
    makeKiddomPostRequest,
  )
  return useCallback(
    (tree: INode) => {
      return request<{ curriculum_id: number; tree: INode }>({
        kiddomPath: '/api/tree',
        requestBody: tree as object,
      })
    },
    [request],
  )
}

export async function copyTree({
  treeId: tree_id,
  versionId: version_id,
}: {
  treeId: string
  versionId: string
}) {
  const query: IQueryInfo = {
    resourceData: { key: 'copyTree', fields: ['tree_id', 'version'] },
    params: { tree_id, version_id },
  }
  const result = await doAdminQueries<{ tree_id: string; version: string }>([
    query,
  ])
  const { tree_id: copiedTreeId, version } = result.data.copyTree
  return { treeId: copiedTreeId, versionId: version }
}

export function goToTree(params: {
  treeId: string
  versionId: string
  nodeId?: string
}) {
  openTab(directTreeAccessUrl(params))
}

export function treeManagementUrl({ treeId, versionId }: ITreeParams) {
  return `/tree_management/${treeId}:${versionId}`
}

export function treeManagementToolUrl({
  treeId,
  versionId,
  nodeId,
  tool,
}: ITreeParams & { tool: string }) {
  const searchParams = new URLSearchParams({
    tMgmt: tool,
  })
  if (nodeId) {
    searchParams.set('nodeId', nodeId)
  }
  return `${treeManagementUrl({ treeId, versionId })}/?${searchParams.toString()}`
}

export function builderUrl(ids: ITreeParams) {
  return treeManagementToolUrl({ ...ids, tool: 'Tree Builder' })
}

export function goToBuilder(node: ITreeParams) {
  openTab(builderUrl(node))
}

async function updateAndPublishProduct(
  params: {
    product_id: number
    version_name: string
  }[],
) {
  const path = `/api/tree/publish`
  const result: string[] = []
  const errors: string[] = []
  const doBatch = async (
    params: {
      product_id: number
      version_name: string
    }[],
  ) => {
    const { result: batchResult, error } = await makeKiddomPostRequest<
      string[]
    >({ kiddomPath: path, requestBody: { params } })
    if (batchResult) result.push(...batchResult)
    if (error) errors.push(error)
  }
  await processInChunks(params, 3, doBatch)
  return { result, errors }
}

export function getMasterInfo(
  params: { treeId: string; version: string }[],
): Promise<{ [key: string]: IMasterInfo }> {
  const queries = params.map(({ treeId, version }, index) => ({
    resourceData: MasterInfoResource,
    params: { tree_id: treeId, version },
    alias: `tree_${index}`,
  }))
  return doAdminQueries(queries).then((result: any) => {
    const byTreeId: {
      [treeId: string]: {
        treeId: string
        version: string
        copiedTrees: string[]
      }
    } = {}
    Object.values(result.data).forEach((r: any) => {
      byTreeId[`${r.tree_id}:${r.version}`] = {
        treeId: r.tree_id,
        version: r.version,
        copiedTrees: r.copied_trees,
      }
    })
    return byTreeId
  })
}

export interface ICurriculaSchool {
  id: number
  name: string
}
export interface ICurriculaInfo {
  title: string
  created_at: string
  deleted: boolean
  id: number
  schools: ICurriculaSchool[]
  master_version?: string
  tree_id: string
  tree_version: string
}

function setDistrictCurriculaDeleted({
  district_curriculum_id,
  deleted,
}: {
  district_curriculum_id: number
  deleted: boolean
}) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'setDistrictCurriculaDeleted',
    },
    params: {
      district_curriculum_id,
      deleted,
    },
  }
  return doAdminQueries([query])
}

function setSchoolCurriculaDeleted({
  curricula_id,
  school_id,
  deleted,
}: {
  curricula_id: number
  school_id: number
  deleted: boolean
}) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'setSchoolCurriculaDeleted',
    },
    params: {
      curricula_id,
      school_id,
      deleted,
    },
  }
  return doAdminQueries([query])
}

function useDeleteSchoolCurricula() {
  return useAdminQueryWithErrorNotification(
    { errorTitle: 'There was an error deleting the curricula' },
    setSchoolCurriculaDeleted,
  )
}

function useDeleteDistrictCurricula() {
  return useAdminQueryWithErrorNotification(
    { errorTitle: 'There was an error deleting the curricula' },
    setDistrictCurriculaDeleted,
  )
}

export function useDeleteCurricula() {
  const deleteSchoolCurricula = useDeleteSchoolCurricula()
  const deleteDistrictCurricula = useDeleteDistrictCurricula()
  return useCallback(
    async (curricula: ICurricula, deleted: boolean) => {
      if (isSchoolCurricula(curricula)) {
        await deleteSchoolCurricula({
          curricula_id: curricula.curriculum_id,
          school_id: curricula.schoolId,
          deleted,
        })
      } else if (isDistrictCurricula(curricula)) {
        await deleteDistrictCurricula({
          district_curriculum_id: curricula.curriculum_id,
          deleted,
        })
      }
    },
    [deleteSchoolCurricula, deleteDistrictCurricula],
  )
}

function deleteSchoolsCurriculaByDistrictIDFn(params: {
  master_tree_id: string
  master_version_id: string
  district_id: number
}) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'deleteSchoolsCurriculaByDistrictID',
    },
    params,
  }
  return doAdminQueries([query])
}

export function useDeleteSchoolsCurriculaByDistrictID() {
  return useAdminQueryWithErrorNotification(
    { errorTitle: 'Error removing curricula from schools' },
    deleteSchoolsCurriculaByDistrictIDFn,
  )
}

export function useDeleteSchoolCurriculaExported() {
  return useDeleteSchoolCurricula()
}

function getCurriculaInDistrict(districtId: number) {
  const query: IQueryInfo = {
    resourceData: GetCurriculumInDistrictsByDistrictID,
    params: {
      district_id: districtId,
    },
  }
  return doAdminQueries<
    {
      id: number
      tree_id: string
      tree_version: string
      deleted: boolean
    }[]
  >([query]).then(({ data, errors }) => {
    if (!data) return { data: [], errors }
    const converted: IDistrictCurricula[] = data.curriculumInDistricts.map(
      (c) => ({
        ...c,
        curriculum_id: c.id,
        districtId,
      }),
    )
    return { data: converted, errors }
  })
}

export async function getCurriculumInDistrictsByDistrictID(districtId: number) {
  const { data } = await getCurriculaInDistrict(districtId)
  return data
}

export function useGetCurriculumInDistrict() {
  return useAdminQueryWithErrorNotification(
    { errorTitle: 'Error getting district curricula' },
    getCurriculaInDistrict,
  )
}

export function nextVersionName(version: string) {
  return version.replace(/\d+$/, (n) => `${parseInt(n) + 1}`)
}

export function useUpdateAndPublishProduct() {
  const products = useAtomValue(allProductsByPrimaryTreeId)
  return useCallback(
    async (
      updates: {
        treeId: string
        newVersionName: string
      }[],
    ) => {
      const { updateParams, errors } = updates.reduce<{
        updateParams: { product_id: number; version_name: string }[]
        errors: string[]
      }>(
        ({ errors, updateParams }, { treeId, newVersionName }) => {
          const product = products[treeId]
          if (!product) {
            return {
              updateParams,
              errors: [...errors, `No Product found for ${treeId}`],
            }
          }
          return {
            errors,
            updateParams: [
              ...updateParams,
              {
                product_id: product.id,
                version_name: newVersionName,
              },
            ],
          }
        },
        { updateParams: [], errors: [] },
      )
      const { result, errors: updateErrors } =
        await updateAndPublishProduct(updateParams)
      return { result, errors: [...errors, ...updateErrors] }
    },
    [products],
  )
}

export async function addDesmos(desmosCode: string, node: INode) {
  switch (node.type) {
    case NodeType.Note:
      return addDesmosToNote(desmosCode, node)
    case NodeType.Question:
      return addDesmosToQuestion(desmosCode, node)
    default:
      return Promise.reject(new Error('Not Implemented'))
  }
}

function appendDesmosHtml(desmosCode: string, currentContent?: string) {
  const toParse = currentContent || '<p></p>'
  const parser = new DOMParser()
  const htmlDoc = parser.parseFromString(toParse, 'text/html')

  const container = htmlDoc.createElement('p')
  const iframe = htmlDoc.createElement('iframe')
  iframe.src = `https://www.desmos.com/calculator/${desmosCode}`
  iframe.setAttribute('style', 'height: 550px; width: 650px;')
  container.appendChild(iframe)

  if (htmlDoc.body.hasChildNodes()) {
    htmlDoc.body.firstChild!.appendChild(container)
  } else {
    htmlDoc.body.appendChild(container)
  }
  return htmlDoc.body.innerHTML
}

async function addDesmosToNote(desmosCode: string, node: INode) {
  node.body!.value = appendDesmosHtml(desmosCode, node.body?.value)
  return updateTreeNode(node)
}

async function addDesmosToQuestion(desmosCode: string, node: INode) {
  const withDesmos = appendDesmosHtml(desmosCode, node.description)
  return updateTreeNode({
    ...retrieveIds(node),
    description: withDesmos,
  })
}

export async function addGeogebraLeaf(geogebraCode: string, node: INode) {
  const leaf = {
    body: {
      material_id: geogebraCode,
    },
    type: LeafTypes.AppletGeogebra,
  }
  return createLeaf({
    tree_id: node.tree_id,
    version: node.version,
    id: node.id,
    leaves: [leaf],
  })
}

export interface INodeMapping {
  id: string
  deleted: boolean
  from_node_id: string
  to_node_id?: string
  user_modified: boolean
}

export interface IVersionMapping {
  from_tree_id: string
  from_version: string
  to_tree_id: string
  to_version: string
  matches: INodeMapping[]
}

export interface IParsedVersionMapping {
  fromTreeId: string
  fromVersion: string
  toTreeId: string
  toVersion: string
  missing: INodeMapping[]
  matched: INodeMapping[]
  duplicates: { [k: string]: INodeMapping[] }
  noFromNode: INodeMapping[]
}

export interface IVersionMappingParams {
  fromTree: { tree_id: string; version: string }
  toTree: { tree_id: string; version: string }
}

function convertVersionMapping(mapping: IVersionMapping) {
  const mappingInfo: IParsedVersionMapping = {
    fromTreeId: mapping.from_tree_id,
    fromVersion: mapping.from_version,
    toTreeId: mapping.to_tree_id,
    toVersion: mapping.to_version,
    missing: [],
    matched: [],
    duplicates: {},
    noFromNode: [],
  }
  const mapCheck: { [k: string]: INodeMapping[] } = {}
  mapping.matches.forEach((m) => {
    if (!m.from_node_id) {
      mappingInfo.noFromNode.push(m)
    } else if (!m.to_node_id && !m.deleted) {
      mappingInfo.missing.push(m)
    } else {
      mappingInfo.matched.push(m)
    }
    if (m.from_node_id && m.to_node_id) {
      if (!mapCheck[m.to_node_id]) {
        mapCheck[m.to_node_id] = []
      }
      mapCheck[m.to_node_id].push(m)
    }
  })
  Object.keys(mapCheck).forEach((key) => {
    if (mapCheck[key].length > 1) {
      mappingInfo.duplicates[key] = mapCheck[key]
    }
  })
  return mappingInfo
}

export async function getVersionMapping({
  fromTree,
  toTree,
}: IVersionMappingParams) {
  const kiddomPath = `/api/tree/v1/versions/migrations/${fromTree.tree_id}/${fromTree.version}/${toTree.tree_id}/${toTree.version}`
  const { result, error } = await makeKiddomGetRequest<IVersionMapping>({
    kiddomPath,
  })
  const converted = result ? convertVersionMapping(result) : undefined
  return {
    result: converted,
    error,
  }
}

export function useGetVersionMapping() {
  return useWithErrorNotification(
    { errorTitle: 'Error Retrieving Mappings' },
    getVersionMapping,
  )
}

async function createVersionMapping({
  fromTree,
  toTree,
}: IVersionMappingParams) {
  const requestBody = {
    from_tree_id: fromTree.tree_id,
    from_version: fromTree.version,
    to_tree_id: toTree.tree_id,
    to_version: toTree.version,
  }
  return makeKiddomPutRequest({
    kiddomPath: `/api/tree/v1/versions/migrate`,
    requestBody,
  })
}
export function useCreateVersionMapping() {
  return useWithErrorNotification(
    { errorTitle: 'Error Creating Version Mapping' },
    createVersionMapping,
  )
}

export interface IVersionMappingUpdate {
  id: string
  deleted: boolean
  to_node_id: string | null
}

async function updateVersionMapping(
  { fromTree, toTree }: IVersionMappingParams,
  updates: IVersionMappingUpdate[],
) {
  const { result, error } = await makeKiddomPutRequest<IVersionMapping>({
    kiddomPath: `/api/tree/v1/versions/migrations/${fromTree.tree_id}/${fromTree.version}/${toTree.tree_id}/${toTree.version}`,
    requestBody: updates,
  })
  const returnResult = result ? convertVersionMapping(result) : result
  return { result: returnResult, error }
}
export function useUpdateVersionMapping() {
  return useWithErrorNotification(
    { errorTitle: 'Error Updating Mapping' },
    updateVersionMapping,
  )
}

export async function addSkillForId(skillsList: INode, skillID: number) {
  const skillLeaf = {
    type: LeafTypes.Skill,
    body: { skillID },
  }
  const tree = await createLeaf({
    tree_id: skillsList.tree_id,
    version: skillsList.version,
    id: skillsList.id,
    leaves: [skillLeaf],
  })
  return tree
}

function cleanTreeContent(tree: INode) {
  const nodes = nodesOfTypeWithContent({
    tree,
    types: [],
    content: ['<script'],
  })
  nodes.forEach((n) => {
    const nodeContent = contentFromNode(n)
    const cleanContent = parseHtmlString(nodeContent).body.innerHTML
    updateNodeContent(cleanContent, n)
  })
}

function filterSubtreeForAppend(subTree: INode) {
  cleanTreeContent(subTree)
  const filteredKeys = [
    'tree_id',
    'version',
    'id',
    'created_at',
    'updated_at',
    'depth',
    'parent_id',
  ]
  const children =
    subTree.nodes
      ?.filter((n) => !isReferenceNode(n))
      .map(filterSubtreeForAppend) ?? []
  const filtered: Partial<INode> = {
    ...subTree,
    nodes: children as INode[],
    leaves: subTree.leaves?.map((l) => {
      const lFiltered: any = { ...l }
      filteredKeys.forEach((k) => delete lFiltered[k])
      return lFiltered
    }),
  }
  filteredKeys.forEach((attr) => delete filtered[attr as keyof INode])
  return filtered
}

// this function returns a function that can be used to copy a source node
// into the child list of a destination parent, optionally setting it before
// the sibling otherwise at the end of the list
export function useCopyNode() {
  const appendRequest = useWithErrorNotification(
    { errorTitle: 'Copy Node Error' },
    appendNodeToTree,
  )
  return useCallback(
    async ({
      sourceNode,
      siblingNode,
      destParent,
    }: {
      sourceNode: INode
      siblingNode?: INode
      destParent: INode
    }) => {
      const cloned = cloneDeep(sourceNode)
      const filtered = filterSubtreeForAppend(cloned)
      const result = await appendRequest({
        ...retrieveIds(destParent),
        next_sibling_node_id: siblingNode?.id,
        ...filtered,
      })
      return result!
    },
    [appendRequest],
  )
}

async function reorderSiblingNodes(moving: INode, thisNodeIsAfter: INode) {
  return updateTreeNode({
    ...retrieveIds(moving),
    next_sibling_node_id: thisNodeIsAfter.id,
  })
}

// this function returns a function that can be used to move a source node
// into the child list of a destination parent, optionally setting it before
// the sibling otherwise at the end of the list
export function useMoveNode() {
  const appendRequest = useWithErrorNotification(
    { errorTitle: 'Move Node Error' },
    appendNodeToTree,
  )
  const reorderRequest = useWithErrorNotification(
    { errorTitle: 'Node Reorder Error' },
    reorderSiblingNodes,
  )
  const deleteRequest = useDeleteNode()
  return useCallback(
    async ({
      sourceNode,
      siblingNode,
      destParent,
    }: {
      sourceNode: INode
      siblingNode?: INode
      destParent?: INode
    }) => {
      if (sourceNode.parent_id === siblingNode?.parent_id) {
        return reorderRequest(sourceNode, siblingNode)
      }

      const cloned = cloneDeep(sourceNode)
      const filtered = filterSubtreeForAppend(cloned)

      if (sourceNode.tree_id === siblingNode?.tree_id) {
        const result = await appendRequest({
          ...retrieveIds(siblingNode),
          id: siblingNode.parent_id,
          next_sibling_node_id: siblingNode?.id,
          ...filtered,
        })
        if (!result) return
        return deleteRequest(idsForNode(sourceNode))
      }

      if (sourceNode.tree_id === destParent?.tree_id) {
        const result = await appendRequest({
          ...retrieveIds(destParent),
          ...filtered,
        })
        if (!result) return
        return deleteRequest(idsForNode(sourceNode))
      }
    },
    [appendRequest, reorderRequest, deleteRequest],
  )
}
